import { CloudUpload } from '@mui/icons-material';
import { ReactElement, useCallback } from 'react';
import { Button, useGetMany, useGetOne, useListContext, useNotify } from 'react-admin';
import type { BillingItemResource } from '../../../Entities/BillingItem';
import type { InvoiceResource } from '../../../Entities/Invoice';
import { DeepPartial, idFrom, not } from '../../../utils';
import type { MerchantResource } from '../../../Entities/Merchant';
import type { RegionResource } from '../../../Entities/Region';
import { useBillingItemPrefetch, useMerchantPrefetch } from './usePrefetch';
import calculateDueDate from './calculateDueDate';
import useCreateInvoice from './useCreateInvoice';
import { validateBillingItems } from './validate';
import { isNil } from 'lodash';
import calculateTransactionDate from './calculateTransactionDate';

export default function NetSuiteButton(): ReactElement | null {
  const billingItemPrefetch = useBillingItemPrefetch();
  const merchantPrefetch = useMerchantPrefetch();
  const notify = useNotify();

  const { selectedIds, resource, onUnselectItems } = useListContext();

  const [createInvoice, createStatus] = useCreateInvoice(selectedIds, {
    deselectIds: onUnselectItems,
    prefetch: billingItemPrefetch,
  });

  const billingItems = useGetMany<BillingItemResource>(resource, {
    ids: selectedIds,
    meta: { prefetch: billingItemPrefetch },
  });

  const merchantId = (billingItems.data ?? [])
    .map((billingItem) => idFrom(billingItem, 'merchant'))
    .find(not(isNil));

  const merchant = useGetOne<MerchantResource>('merchants', {
    id: merchantId,
    meta: { prefetch: merchantPrefetch },
  });
  const regionId = idFrom(merchant.data, 'region');

  const region = useGetOne<RegionResource>('regions', { id: regionId });
  const currencyCode = region.data?.attributes.currency_code;

  const handleClick = useCallback(() => {
    const validatedItems = validateBillingItems(billingItems);

    if (validatedItems === undefined) {
      return; // No error, still loading
    }

    if (typeof validatedItems === 'string') {
      return notify(validatedItems, { type: 'error' });
    }

    if (isNil(merchantId)) {
      return notify('No merchant selected', { type: 'error' });
    }

    if (isNil(currencyCode)) {
      return notify("Currency code not defined for merchant's region, but is required.", {
        type: 'error',
      });
    }

    const paymentTypeId = validatedItems.data
      .map((bi) => idFrom(bi, 'billing_item_payment_type'))
      .find(not(isNil));

    const now = new Date();
    const txDate = calculateTransactionDate(billingItems.data ?? []).toISOString();
    const dueDate = calculateDueDate(now, paymentTypeId ?? '').toISOString();

    const invoice: DeepPartial<InvoiceResource> = {
      attributes: {
        currency_code: currencyCode,
        created_date: txDate,
        due_date: dueDate,
        transaction_date: txDate,
      },
      relationships: {
        merchant: { data: { type: 'merchants', id: merchantId } },
        items: {
          data: validatedItems.data.map((item) => ({
            type: item.type,
            id: item.id,
          })),
        },
      },
    };

    createInvoice(invoice);
  }, [createInvoice, currencyCode, billingItems, merchantId, notify]);

  if (resource !== 'billing_items') {
    return null;
  }

  const isDisabled =
    billingItems.isPending ||
    billingItems.isLoading ||
    merchant.isPending ||
    merchant.isLoading ||
    region.isPending ||
    region.isLoading ||
    createStatus.isPending ||
    createStatus.isLoading;

  return (
    <Button label="Upload to NetSuite" onClick={handleClick} disabled={isDisabled}>
      <CloudUpload />
    </Button>
  );
}
